import React from 'react';
import styled from 'styled-components';
import {isSameMonth, isWithinRange, differenceInDays} from 'date-fns';
import DATEFORMAT from '../../../constants/dateformat';
import FlatButton from '../FlatButton';

const Wrapper = styled.div`
  flex-direction: row;
  margin-top: ${({theme}) => theme.spacingHorizontal()};
`;

const Body = styled.div`
  background: ${({theme}) => theme.white};
`;

const Week = styled.div`
  display: flex;
`;

const Head = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${({theme, spacingBottom}) => (spacingBottom ? theme.spacingHorizontal() : null)};
`;

const Button = styled(FlatButton)`
  width: calc(100% / 7);
  text-align: center;
  font-weight: bold;
  outline: none;
  color: ${({theme}) => theme.white};
  background: ${({theme}) => theme.primaryColor};
`;

const MonthText = styled.div`
  flex: 1;
  text-align: center;
  border: 0px;
  font-weight: bold;
  padding: ${({theme}) => theme.spacingHorizontal()} 0;
  color: ${({theme}) => theme.white};
  background: ${({theme}) => theme.primaryColor};
`;

const Day = styled.div`
  width: calc(100% / 7);
  text-align: center;
  padding: ${({theme}) => theme.spacingHorizontal()} 0;
  font-weight: ${({highlight}) => (highlight ? 'bold' : null)};
  cursor: ${({disabled, highlight}) => (highlight ? 'default' : disabled ? 'not-allowed' : 'pointer')};
  background: ${({disabled, isInRange, isSelected, highlight, theme}) =>
    disabled
      ? null
      : isSelected
        ? theme.primaryColor
        : highlight
          ? theme.primaryColor
          : isInRange
            ? theme.white
            : theme.lightGray};
  &:hover {
    background: ${({highlight, theme}) => (highlight ? theme.primaryColor : null)};
  }
  color: ${({disabled, isSelected, highlight, theme}) =>
    disabled ? theme.lightGray : isSelected ? theme.white : highlight ? theme.white : theme.black};
`;

const dateIsInRange = (date, startDate, endDate) => startDate && endDate && isWithinRange(date, startDate, endDate);

const isSelected = (date, compareDate) => compareDate && differenceInDays(date, compareDate) === 0;

function CalendarComponent(props) {
  const {
    startDate,
    endDate,
    getFormattedDate,
    getWeeksInMonth,
    getDateNextMonth,
    getDatePrevMonth,
    setDate,
    setSelectedDate,
    date,
  } = props;

  const weekdays = ['Mon', 'Tue', 'Wed', 'Tue', 'Fri', 'Sat', 'Sun'];

  const currentDate = getFormattedDate('MMM YYYY');
  const weeksInMonth = getWeeksInMonth();

  const setDateNextMonth = () => setDate(getDateNextMonth());
  const setDatePrevMonth = () => setDate(getDatePrevMonth());

  const selectDay = dateValue => () => setSelectedDate(dateValue);
  const isDisabled = dateValue => !isSameMonth(date, dateValue);

  return (
    <Wrapper>
      {(startDate || endDate) && (
        <Head spacingBottom>
          <MonthText>{startDate && getFormattedDate(startDate, DATEFORMAT)}</MonthText>
          <MonthText>
            {endDate && differenceInDays(endDate, startDate) !== 0 && getFormattedDate(endDate, DATEFORMAT)}
          </MonthText>
        </Head>
      )}
      <Head>
        <Button onClick={setDatePrevMonth}>&larr;</Button>
        <MonthText>{currentDate}</MonthText>
        <Button onClick={setDateNextMonth}>&rarr;</Button>
      </Head>
      <Body>
        <Week>
          {weekdays.map((day, i) => (
            <Day highlight key={i}>
              {day}
            </Day>
          ))}
        </Week>
        {weeksInMonth.map((week, i) => (
          <Week key={i}>
            {week.map(day => (
              <Day
                key={day.label}
                disabled={isDisabled(day.dateValue)}
                onClick={isDisabled(day.dateValue) ? null : selectDay(day.dateValue)}
                isInRange={dateIsInRange(day.dateValue, startDate, endDate)}
                isSelected={isSelected(day.dateValue, startDate) || isSelected(day.dateValue, endDate)}>
                {day.label}
              </Day>
            ))}
          </Week>
        ))}
      </Body>
    </Wrapper>
  );
}

export default CalendarComponent;
